import { useCallback } from "react";
import { Navigate, NavigateOptions, To, useNavigate } from "react-router-dom";

import { useAppSelector } from "~/app/store";
import { selectUsersFulfillmentCenter } from "~/redux/selectors/storeSelectors";

// Helper: the shared ?fc= rule, so the hook and component stay in sync.
/** Applies the same ?fc= routing rules as {@link useFcNavigate} to a declarative target. */
export function appendFcToNavigateTarget(
  to: Exclude<To, number>,
  abbreviation: string | undefined
): To {
  if (!abbreviation) {
    return to;
  }

  if (typeof to === "string") {
    const url = new URL(to, window.location.origin);
    url.searchParams.set("fc", abbreviation);
    return url.pathname + url.search;
  }

  const params = new URLSearchParams(to.search || "");
  params.set("fc", abbreviation);
  return { ...to, search: params.toString() };
}

// Component: the declarative <Navigate> form for route guards/redirects.
type FcNavigateProps = {
  to: string;
  replace?: boolean;
};

/** Declarative redirect that preserves ?fc= like {@link useFcNavigate}. */
export function FcNavigate({ to, replace = true }: FcNavigateProps) {
  const fc = useAppSelector(selectUsersFulfillmentCenter);
  return (
    <Navigate
      to={appendFcToNavigateTarget(to, fc?.abbreviation)}
      replace={replace}
    />
  );
}

// Hook: the imperative form for event handlers and effects.
/**
 * Wraps useNavigate to append ?fc=<abbreviation> on every navigation.
 * AGC uses this query parameter to route requests to the correct
 * deployment per fulfillment center.
 *
 * Pre-FC routes (login, callback) navigate without the param since
 * the FC hasn't been selected yet — AGC routes those to the newest instance.
 */
export function useFcNavigate() {
  const navigate = useNavigate();
  const fc = useAppSelector(selectUsersFulfillmentCenter);

  return useCallback(
    (to: To | number, options?: NavigateOptions) => {
      if (typeof to === "number") {
        navigate(to);
        return;
      }

      navigate(appendFcToNavigateTarget(to, fc?.abbreviation), options);
    },
    [navigate, fc]
  );
}
